import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of, retry, tap, timer } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '@services/auth-state-services/auth.service';
import { PersonDto, TermsAndConditionsApiService } from '@bpp-stella/api/stella';
import { StartupTraceUtil } from '@shared/utils/startup-trace.util';

@Injectable({
  providedIn: 'root'
})
export class TermsAndConditionsService {
  private readonly termsAndConditionsApiService = inject(TermsAndConditionsApiService);
  private readonly authService = inject(AuthService);

  public checkIfUserHasTermsAndConditionsAccepted(): Observable<boolean> {
    if (!this.authService.person()?.personId) {
      return of(false);
    }

    return this.termsAndConditionsApiService.apiTermsAndConditionsAcceptedPersonIdGet(this.authService.person()!.personId!).pipe(
      retry({
        count: 2,
        // A 4xx answer is final — only transport failures and server errors can heal on a retry.
        delay: (error: unknown) => {
          if (error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500) {
            throw error;
          }

          return timer(500);
        }
      }),
      tap(() => StartupTraceUtil.mark('terms-check-done')),
      // The gate guards a legal acceptance, so an answer the app could not obtain must send the
      // user to the terms screen rather than through it. Never rethrow: this observable feeds the
      // app's only routing subscription.
      catchError(() => {
        StartupTraceUtil.mark('terms-check-done', 'error');

        return of(false);
      })
    );
  }

  public acceptTermsAndConditions(): Observable<PersonDto> {
    const personId = this.authService.person()?.personId;
    if (!personId) {
      return of();
    }

    return this.termsAndConditionsApiService.apiTermsAndConditionsAcceptPersonIdPost(personId);
  }
}
